'use client'

import _ from 'lodash'
import { twMerge } from 'tailwind-merge'
import { useCallback } from 'react'
import slugify from 'slugify'
import { useQuery } from '@tanstack/react-query'
import Link from 'next/link'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'

import { Vendor } from '@cruz-com/lookup'
import { vendorApi } from '@/api'
import { ChatBubbleBottomCenterTextIcon, PhoneIcon, MapIcon, Squares2X2Icon } from '@heroicons/react/24/outline'
import { lead } from '@/lib'
import { pages } from '@/settings'
import { useGlobalStore } from '@/store/provider'

type Props = {
  id: string | any
  initialData?: Vendor.Location | any
  showMessageDealer: boolean
  showLinkToInventory: boolean
  showRequestHistoryReport?: boolean
}

const btnStyle = 'flex flex-col items-center justify-center sm:flex-row gap-x-3 gap-y-1 px-4 py-3 sm:py-4 text-sm font-medium truncate w-full'

export function DealerLocationTop({ id, initialData, showMessageDealer, showLinkToInventory, showRequestHistoryReport }: Props) {
  const { setLeadType, scrollTopOver } = useGlobalStore((state) => state)

  const { data }: { data: Vendor.Location } = useQuery({
    queryKey: [`Location_${id}`, id],
    queryFn: () => vendorApi.getLocation(id as string),
    staleTime: 3000,
    initialData,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false
  })

  const { name, address1, city, state_abbr, zip_code, phone, distance, place } = data || {}

  const gridCols = useCallback(() => {
    let x = 4
    if (!showMessageDealer) {
      x -= 1
    }
    if (!place?.google_map_uri) {
      x -= 1
    }
    if (!showLinkToInventory) {
      x -= 1
    }
    return `grid-cols-${x}`
  }, [id, place, showLinkToInventory, showMessageDealer])

  const dealerHref = useCallback(() => {
    return pages.dealerVehicles.href
      .replace('[dealer]', slugify(place?.display_name || name || ''))
      .replace('[location]', slugify(`${city} ${state_abbr} ${zip_code}`))
      .replace('[id]', id)
  }, [id, name])

  if (_.isEmpty(data)) return <></>

  const leadTypeList = showRequestHistoryReport
    ? lead.leadList
    : _.filter(lead.leadList, x => x.id !== lead.LeadTypeEnum.RequestHistoryReport)


  const MessageDealerMenu = () =>
    <Popover as='div' className='relative'>
      <PopoverButton className={twMerge(btnStyle, 'focus-visible:outline-none')}>
        <ChatBubbleBottomCenterTextIcon className='size-5' />
        <span>Contact</span>
      </PopoverButton>
      <PopoverPanel anchor='bottom start' transition={true} modal={false} className='menu-items divide-y w-[300px]'>
        {
          _.map(leadTypeList, ({ id, name }) =>
            <button key={`dml-${id}`} type='button'
              onClick={() => setLeadType(id)}
              className='w-full px-6 py-2.5 text-left font-medium'
            >
              {name}
            </button>
          )
        }
      </PopoverPanel>
    </Popover >


  return (
    <>
      <div className='px-4 mb-3 lg:hidden'>
        <div className='text-lg font-bold'>
          {/* {place?.display_name || name || ''} */}
          {name}
        </div>
        <div>
          <address className='not-italic text-sm/6 font-light'>
            {
              // place?.full_address || `${address1}, ${city}, ${state_abbr} ${zip_code}`
              `${address1}, ${city}, ${state_abbr} ${zip_code}`
            }
            {distance && ` (${distance.toLocaleString()} mi. away)`}
          </address >
        </div>
      </div>

      <div className={
        twMerge('sticky top-0 right-0 left-0 z-20 lg:hidden mb-8',
          'border-t border-b grid divide-x dark:bg-zinc-800',
          gridCols(),
          scrollTopOver && 'shadow-xl bg-zinc-900 text-white dark:!bg-zinc-300 dark:text-black'
        )
      }>

        {
          (showMessageDealer) &&
          <MessageDealerMenu />
        }
        <a href={`tel:${phone}`} rel='noreferrer' className={btnStyle}>
          <PhoneIcon className='size-5' />
          <span>Call</span>
        </a>
        {
          place?.google_map_uri &&
          <a href={place.google_map_uri} className={btnStyle} target='_blank' rel='noreferrer'>
            <MapIcon className='size-5' />
            <span>Maps</span>
          </a>
        }
        {
          showLinkToInventory &&
          <Link href={dealerHref()} className={btnStyle} hrefLang='en'>
            <Squares2X2Icon className='size-5' />
            <span>Inventory</span>
          </Link>
        }
      </div >
    </>
  )
}